import { gql } from '@apollo/client';
import {
  CONTACT_BLOCKING_FIELDS,
  CONTACT_INTERACTION_COMPONENT_STATUS,
  CORE_ATTACHMENTS_FIELDS_WITH_REACTIONS_AND_RELATED,
  CORE_CHAT_MESSAGES_FIELDS_WITH_REACTIONS_AND_RELATED,
  CORE_REACTION_FIELDS,
  CONTACT_INTERACTION_PARTICIPATING_ENTITY_CORE_FIELDS,
  CONTACT_INTERACTION_WHATSAPP_TEMPLATE_COMPONENT_BUTTONS_FRAGMENT,
} from 'src/components/providers/apollo-provider-provider';

export const CONVERSATION_FOR_CONTACT_INTERACTION_LIST = gql`
  ${CORE_REACTION_FIELDS}
  ${CORE_ATTACHMENTS_FIELDS_WITH_REACTIONS_AND_RELATED}
  ${CORE_CHAT_MESSAGES_FIELDS_WITH_REACTIONS_AND_RELATED}
  ${CONTACT_INTERACTION_COMPONENT_STATUS}
  ${CONTACT_BLOCKING_FIELDS}
  ${CONTACT_INTERACTION_PARTICIPATING_ENTITY_CORE_FIELDS}
  ${CONTACT_INTERACTION_WHATSAPP_TEMPLATE_COMPONENT_BUTTONS_FRAGMENT}
  query GetConversationForContactInteractionList(
    $id: String!
    $last: Int
    $after: String
    $first: Int
    $before: String
    $where: ConversationItemsWhereInput
  ) {
    conversation(id: $id) {
      id
      isMarked
      isOpen
      isReplyPending
      isSnoozed
      isStarted
      snoozedUntil
      updatedAt
      closedBy {
        id
        fullName
      }
      closedAt
      aiAgent {
        id
        name
        friendlyName
      }
      contact {
        id
        label
        primaryEmail
        firstName
        lastName
        fullName
        primaryPhone {
          national
          regionCode
        }
        secondaryPhone {
          national
          regionCode
        }
        ...ContactBlockingFieldsFragment
        status {
          id
          name
        }
        tags {
          id
          name
        }
        stage {
          id
          slug
        }
        assignee {
          id
        }
        lastConversation {
          id
        }
      }
      lastContactInteraction {
        ... on ContactInteraction {
          id
          occurredAt
          isMasked
        }
        ... on ContactInteractionPhoneCall {
          callDetail {
            isInProgress
          }
        }
      }
      items(last: $last, before: $before, after: $after, first: $first, where: $where) {
        pageInfo {
          hasPreviousPage
          hasNextPage
        }
        edges {
          cursor
          node {
            ... on ContactInteraction {
              id
              isMasked
              occurredAt
              createdAt
              createdByUser {
                id
              }
              description
              resourceAddress
              contactAddress
              participatingEntity {
                ...ContactInteractionParticipatingEntityFragment
              }
              registrationType
              source {
                id
                name
              }
              campaign {
                id
                name
              }
              department {
                id
                name
              }
            }
            ... on ContactInteractionWithDirection {
              direction
            }
            ... on ContactInteractionWithAttachments {
              ...ContactInteractionAttachmentsFragmentWithReactionsAndRelated
            }
            ... on ContactInteractionChatMessage {
              ...ContactInteractionChatMessagesFragmentWithReactionsAndRelated
            }
            ... on ContactInteractionWhatsapp {
              template {
                id
                ...ContactInteractionComponentStatus
                name
                language
                ...ContactInteractionComponentReactionsFragment
                components {
                  ... on ContactInteractionWhatsappTemplateComponent {
                    text
                    format
                  }
                  ... on ContactInteractionWhatsappTemplateComponentBody {
                    variables {
                      text
                      parameterName
                    }
                  }
                  ... on ContactInteractionWhatsappTemplateComponentHeader {
                    variables {
                      text
                      parameterName
                    }
                  }
                  ... on ContactInteractionWhatsappTemplateComponentMediaHeader {
                    format
                    example {
                      headerHandle
                    }
                  }
                  ...ContactInteractionWhatsappTemplateComponentButtonsFragment
                }
              }
            }
            ... on ContactInteractionFacebookAd {
              fields {
                key
                label
                value
              }
            }
            ... on ContactInteractionEmail {
              subject
              bodyText
              bodyHtml
            }
            ... on ContactInteractionPhoneCall {
              callDetail {
                duration
                waitTime
                talkTime
                inboundLastStep
                outboundDialUnansweredReason
                isInProgress
                brief {
                  ... on ContactInteractionBrief {
                    text
                  }
                  ... on AsyncStatusPending {
                    asyncStatus
                  }
                  ... on ContactInteractionBriefDisabled {
                    asyncStatus
                  }
                }
                summary {
                  ... on ContactInteractionSummary {
                    text
                  }
                  ... on AsyncStatusPending {
                    asyncStatus
                  }
                  ... on ContactInteractionSummaryDisabled {
                    asyncStatus
                  }
                }
                sentimentAnalysis {
                  ... on ContactInteractionSentimentAnalysis {
                    id
                    phrases {
                      id
                      emotions
                      end
                      phrase
                      sentiment
                      speakerType
                      start
                    }
                    totalNegatives
                    totalPositives
                  }
                  ... on AsyncStatusPending {
                    asyncStatus
                  }
                  ... on SentimentAnalysisDisabled {
                    asyncStatus
                  }
                }
                transcription {
                  ... on ContactInteractionTranscription {
                    id
                    phrases {
                      edges {
                        cursor
                        node {
                          id
                          emotions
                          end
                          phrase
                          sentiment
                          speakerType
                          start
                        }
                      }
                    }
                    contact {
                      id
                      label
                      status {
                        id
                        name
                      }
                      stage {
                        id
                        slug
                      }
                    }
                    user {
                      id
                      fullName
                      avatar {
                        color
                        initials
                      }
                    }
                  }
                  ... on AsyncStatusPending {
                    asyncStatus
                  }
                  ... on ContactInteractionTranscriptionDisabled {
                    asyncStatus
                  }
                }
              }
              aiAgentSessions {
                id
                status
                aiAgent {
                  id
                  name
                  friendlyName
                }
                goals {
                  id
                  name
                  status
                }
                toolCalls {
                  id
                  name
                }
              }
              dialStatus
              recording {
                url
              }
            }
            ... on ConversationNote {
              id
              text
              rawNote
              createdAt
              createdBy {
                id
                fullName
              }
              aiAgentSession {
                id
                summary
                status
                aiAgent {
                  id
                  name
                  friendlyName
                }
                goals {
                  id
                  name
                  status
                }
                toolCalls {
                  id
                  name
                }
                takenOverBy {
                  id
                  fullName
                  role {
                    id
                    name
                  }
                  avatar {
                    color
                    initials
                    url(transform: { width: 180, height: 180 })
                  }
                }
              }
            }
            ... on ContactNote {
              id
              text
              createdAt
              user {
                id
                fullName
              }
            }
            ... on ContactTimeline {
              id
              occurredAt
            }
            ... on ContactTimelineWithOriginator {
              originator {
                id
                fullName
              }
            }
            ... on ContactTimelineWithStageAndStatus {
              oldStatus {
                id
                name
              }
              newStatus {
                id
                name
              }
              oldStage {
                id
                name
              }
              newStage {
                id
                name
              }
            }
            ... on ContactTimelineContactCreated {
              id
            }
            ... on ContactTimelineContactUpdated {
              id
            }
            ... on ContactTimelineWithTask {
              task {
                id
                title
              }
            }
            ... on ContactTimelineTaskStatusUpdated {
              oldTaskStatus
              newTaskStatus
            }
            ... on ContactTimelineTaskDeleted {
              taskTitle
            }
            ... on ContactTimelineWithSchedule {
              schedule {
                id
                title
              }
            }
            ... on ContactTimelineScheduleStatusUpdated {
              newScheduleStatus
              oldScheduleStatus
            }
            ... on ContactTimelineScheduleDeleted {
              appointmentTitle
            }
            ... on ContactTimelineAiAgentChatSessionTakenOver {
              aiAgentSession {
                id
                aiAgent {
                  id
                  name
                  friendlyName
                }
                takenOverBy {
                  id
                  fullName
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const CONVERSATION_WITH_LAST_INTERACTION = gql`
  query GetConversation($id: String!) {
    conversation(id: $id) {
      id
      lastContactInteraction {
        ... on ContactInteraction {
          id
          occurredAt
        }
        ... on ContactInteractionWithDirection {
          direction
        }
        ... on ContactInteractionPhoneCall {
          callDetail {
            isInProgress
          }
        }
      }
    }
  }
`;

export const CONVERSATION_UNSNOOZE = gql`
  mutation ConversationUnsnooze($conversationId: String!) {
    conversationUnsnooze(conversationId: $conversationId) {
      conversation {
        id
        isSnoozed
        snoozedUntil
        snoozedBy {
          id
        }
      }
    }
  }
`;
